import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import useBlockStyles from '../common/useBlockStyles';
import { resolveLink, isInternalLink } from '../../../../utils/linkResolver';

const STYLE_OPTIONS = {
  defaultBackground: 'section',
  defaultHeadingSize: 'medium',
  defaultAlignment: 'center',
  defaultPadding: 'normal',
};

const Card = ({ card, showImages, cardTextAlign, styles, username, isOwn }) => {
  const cardHref = card.ctaText && card.ctaLink ? resolveLink(card.ctaLink, username, isOwn) : null;
  const cardInternal = cardHref ? isInternalLink(cardHref) : false;

  return (
    <div
      className="group relative rounded-2xl overflow-hidden flex flex-col h-full"
      style={{
        backgroundColor: styles.cardBackground,
        border: `1px solid ${styles.accentColor}35`,
        boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
        transition: 'transform 0.4s, box-shadow 0.4s, border-color 0.4s',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = 'translateY(-3px)';
        e.currentTarget.style.boxShadow = `0 16px 40px rgba(0,0,0,0.2), 0 0 0 1px ${styles.accentColor}20`;
        e.currentTarget.style.border = `1px solid ${styles.accentColor}60`;
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = 'none';
        e.currentTarget.style.boxShadow = '0 2px 12px rgba(0,0,0,0.08)';
        e.currentTarget.style.border = `1px solid ${styles.accentColor}35`;
      }}
    >

      {showImages && card.image && (
        <div
          className="h-36 sm:h-44 md:h-48 overflow-hidden flex items-center justify-center"
          style={{ backgroundColor: styles.isDark ? 'rgba(0,0,0,0.2)' : 'rgba(0,0,0,0.03)' }}
        >
          <img
            src={card.image}
            alt={card.title || 'Card image'}
            className="max-w-full max-h-full object-contain transition-transform duration-500 group-hover:scale-[1.03]"
          />
        </div>
      )}

      <div className="p-5 sm:p-6 flex flex-col flex-grow" style={{ textAlign: cardTextAlign }}>
        {card.title && (
          <h4
            className="text-base sm:text-lg font-bold mb-2 leading-snug"
            style={{ color: styles.headingColor }}
          >
            {card.title}
          </h4>
        )}
        {card.description && (
          <p
            className="text-sm leading-[1.7] whitespace-pre-wrap flex-grow"
            style={{ color: `${styles.mutedColor}dd` }}
          >
            {card.description}
          </p>
        )}
        {cardHref && (() => {
          const linkClass = "inline-flex items-center gap-1.5 mt-4 text-sm font-semibold transition-all duration-300 hover:gap-2.5";
          const linkStyle = { color: styles.accentColor };
          const inner = (
            <>
              {card.ctaText}
              <ArrowRight size={13} className="transition-transform duration-300 group-hover:translate-x-0.5" />
            </>
          );
          return cardInternal ? (
            <Link to={cardHref} className={linkClass} style={linkStyle}>{inner}</Link>
          ) : (
            <a href={cardHref} target="_blank" rel="noopener noreferrer" className={linkClass} style={linkStyle}>{inner}</a>
          );
        })()}
      </div>
    </div>
  );
};

const CardGridRenderer = ({ block, theme = {}, username }) => {
  const { content } = block;
  const {
    heading,
    items = [],
    columns = 3,
    showImages = true,
    cardTextAlign = 'left',
  } = content;

  const styles = useBlockStyles(content, theme, STYLE_OPTIONS);
  const isOwn = !window.location.pathname.startsWith('/me/');

  const getGridCols = () => {
    switch (columns) {
      case 2:
        return 'sm:grid-cols-2';
      case 4:
        return 'sm:grid-cols-2 lg:grid-cols-4';
      case 3:
      default:
        return 'sm:grid-cols-2 lg:grid-cols-3';
    }
  };

  return (
    <div
      className={`w-full ${styles.sectionPaddingClass}`}
      style={{ backgroundColor: styles.sectionBackground }}
    >
      <div className="max-w-6xl mx-auto">
        {heading && (
          <h3
            className={`${styles.headingSizeClass} font-bold ${styles.headingAlignmentClass} mb-6 sm:mb-8 md:mb-10`}
            style={{ color: styles.headingColor, fontFamily: styles.headingFontStyle }}
          >
            {heading}
          </h3>
        )}

        <div className={`grid grid-cols-1 ${getGridCols()} gap-5 sm:gap-6`}>
          {items.map((card) => (
            <Card
              key={card.id}
              card={card}
              showImages={showImages}
              cardTextAlign={cardTextAlign}
              styles={styles}
              username={username}
              isOwn={isOwn}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default CardGridRenderer;
